import {LEAGUES} from '../../constants/leagues';
import {QUEST_TYPES, QUESTS} from '../../configs/quests';
import {getTutorialChapterReward} from '../tutorial/getTutorialChapterReward';
import {BOT_NAMES} from '../../constants/botNames';
import {PLAYER_COLORS} from '../../configs/colors';
import {FACTION_IDS_IMPLEMENTED} from '../../configs/factions';
import {ALL_FACTION_SKINS} from '../../configs/factions/skins/allFactionSkins';

/**
 * Что показать в админском разделе «Тест модалок»: список наградных модалок
 * конца матча с готовыми параметрами.
 *
 * Список строится из НАСТОЯЩИХ конфигов заданий, а не перечисляется руками —
 * иначе после правки наград предпросмотр показывал бы вчерашнюю картинку.
 * У задания с наградой-фракцией вариантов два: сама фракция и запасная
 * награда для игрока, у которого фракция уже открыта (см.
 * getTutorialChapterReward) — визуал у них разный, нужны оба.
 */

// Модалки конца матча, по типу награды (см. GameEndModal)
export const REWARD_MODAL_KINDS = {
  CHEST: 'chest',
  FACTION: 'faction',
  RANK_SKIN: 'rank_skin',
  MATCH_WINNER: 'match_winner',
};

// Задания, которые закрываются завершением матча, — только их награды
// показываются наградными модалками после итогов
const MATCH_END_QUEST_TYPES = [
  QUEST_TYPES.LOCAL_MATCH, QUEST_TYPES.ONLINE_MATCH, QUEST_TYPES.RANKED_MATCH, QUEST_TYPES.LEAGUE,
];

// Фракция для предпросмотра ранговых образов: сам образ у каждой фракции свой,
// но верстка модалки от фракции не зависит — хватает одной
const RANK_SKIN_PREVIEW_FACTION_ID = 'humans';

const buildRewardPreview = (quest, reward, titleSuffix) => {
  if (!reward) {
    return null;
  }
  const title = titleSuffix ? `${quest.title} — ${titleSuffix}` : quest.title;
  if (reward.type === 'chest') {
    return {id: `${quest.id}_chest`, title, kind: REWARD_MODAL_KINDS.CHEST, chestId: reward.chestId};
  }
  if (reward.type === 'faction') {
    return {id: `${quest.id}_faction`, title, kind: REWARD_MODAL_KINDS.FACTION, factionId: reward.factionId};
  }
  return null;
};

/**
 * Наградные модалки заданий, закрывающихся концом матча.
 * @returns {Array<{id: string, title: string, kind: string, chestId?: string, factionId?: string}>}
 */
export const getQuestRewardPreviews = () => {
  return QUESTS
    .filter((quest) => MATCH_END_QUEST_TYPES.includes(quest.type))
    .flatMap((quest) => {
      const defaultReward = getTutorialChapterReward(quest, {});
      const ownedReward = getTutorialChapterReward(quest, {isAllFactionsAvailable: true});
      // Награда одна и та же — запасного варианта у задания нет
      if (defaultReward === ownedReward) {
        return [buildRewardPreview(quest, defaultReward, '')];
      }
      return [
        buildRewardPreview(quest, defaultReward, 'фракция'),
        buildRewardPreview(quest, ownedReward, 'фракция уже открыта'),
      ];
    })
    .filter(Boolean);
};

/**
 * Модалки ранговых образов: их дают за высшие лиги в конце рейтингового матча
 * (calculateRankSkinRewards), поэтому им место в том же разделе.
 * @returns {Array<{id: string, title: string, kind: string, skinId: string}>}
 */
export const getRankSkinRewardPreviews = () => {
  return [
    {league: LEAGUES.GOLD, title: 'Ранговый образ Золотой Лиги'},
    {league: LEAGUES.DIAMOND, title: 'Ранговый образ Алмазной Лиги'},
    {league: LEAGUES.RUBY, title: 'Ранговый образ Рубиновой Лиги'},
  ].map(({league, title}) => ({
    id: `rank_skin_${league}`,
    title,
    kind: REWARD_MODAL_KINDS.RANK_SKIN,
    skinId: `${RANK_SKIN_PREVIEW_FACTION_ID}_${league}`,
  }));
};

/**
 * Торжество победителя матча (MatchWinnerModal) — показывается в конце каждого
 * матча перед наградами. Варианты разные по виду: обычный образ фракции,
 * ранговый, купленный скин и длинный никнейм на светлом цвете игрока.
 * @returns {Array<{id: string, title: string, kind: string, player: Object, finalWinPoints: number}>}
 */
export const getMatchWinnerPreviews = () => ([
  {
    id: 'match_winner_default',
    title: 'Победитель — образ фракции',
    player: {name: 'Полководец', color: 'red', factionId: 'humans', selectedSkin: 'humans_default'},
    finalWinPoints: 141,
  },
  {
    id: 'match_winner_rank_skin',
    title: 'Победитель рейтинговой партии — ранговый образ, рейтинг',
    player: {name: 'shadow_fox_88', color: 'dark_green', factionId: 'ratmen', selectedSkin: 'ratmen_gold'},
    finalWinPoints: 97,
    isRanked: true,
    ratingDelta: 31,
    rating: 1531,
  },
  {
    id: 'match_winner_skin',
    title: 'Победитель — скин фракции',
    player: {name: 'Владыка Севера', color: 'sky_blue', factionId: 'aquars', selectedSkin: 'aquars_north'},
    finalWinPoints: 118,
  },
  {
    id: 'match_winner_long_name',
    title: 'Победитель — длинный никнейм, светлый цвет',
    player: {name: 'НепобедимыйСтратегЮжныхЗемель', color: 'yellow', factionId: 'elves', selectedSkin: null},
    finalWinPoints: 203,
  },
].map((preview) => ({...preview, kind: REWARD_MODAL_KINDS.MATCH_WINNER})));

// Цвета, которые бывают у игрока в матче: нейтральный и зрительский — не игроки
const MATCH_WINNER_PREVIEW_COLORS = Object.values(PLAYER_COLORS)
  .filter((color) => color !== PLAYER_COLORS.NEUTRAL && color !== PLAYER_COLORS.SPECTATOR_COLOR);

// Разброс итоговых очков победителя: от скромной победы до разгрома
const MATCH_WINNER_PREVIEW_MIN_WIN_POINTS = 40;
const MATCH_WINNER_PREVIEW_MAX_WIN_POINTS = 260;
// Рейтинг победителя рейтинговой партии: все лиги, прирост победы
const MATCH_WINNER_PREVIEW_MAX_RATING = 3200;
const MATCH_WINNER_PREVIEW_MAX_RATING_DELTA = 40;

// Образы фракции, в которых победитель может прийти на матч: обычный,
// ранговые (золото, алмаз, рубин) и скины из конфига
export const getMatchWinnerPreviewSkinIds = (factionId) => [
  `${factionId}_default`,
  `${factionId}_${LEAGUES.GOLD}`,
  `${factionId}_${LEAGUES.DIAMOND}`,
  `${factionId}_${LEAGUES.RUBY}`,
  ...ALL_FACTION_SKINS.filter((skin) => skin.factionId === factionId).map((skin) => skin.id),
];

const pickRandom = (items, random) => items[Math.min(items.length - 1, Math.floor(random() * items.length))];

/**
 * Случайный победитель для предпросмотра MatchWinnerModal: никнейм из перечня
 * ботов, цвет игрока, реализованная фракция, один из ее образов, итоговые
 * очки, а через раз — рейтинговая партия с изменением рейтинга и новым
 * рейтингом. Поздравление и кнопку модалка выбирает случайно сама.
 * @param {Function} [random] - источник случайности (Math.random по умолчанию)
 */
export const createRandomMatchWinnerPreview = (random = Math.random) => {
  const factionId = pickRandom(FACTION_IDS_IMPLEMENTED, random);
  const player = {
    name: pickRandom(BOT_NAMES, random),
    color: pickRandom(MATCH_WINNER_PREVIEW_COLORS, random),
    factionId,
    selectedSkin: pickRandom(getMatchWinnerPreviewSkinIds(factionId), random),
  };
  const finalWinPoints = MATCH_WINNER_PREVIEW_MIN_WIN_POINTS
    + Math.floor(random() * (MATCH_WINNER_PREVIEW_MAX_WIN_POINTS - MATCH_WINNER_PREVIEW_MIN_WIN_POINTS + 1));
  const isRanked = random() < 0.5;
  const ratingInfo = isRanked
    ? {
      ratingDelta: 1 + Math.floor(random() * MATCH_WINNER_PREVIEW_MAX_RATING_DELTA),
      rating: Math.floor(random() * (MATCH_WINNER_PREVIEW_MAX_RATING + 1)),
    }
    : {};
  const resultText = isRanked
    ? `рейтинг +${ratingInfo.ratingDelta} → ${ratingInfo.rating}`
    : `${finalWinPoints} очков`;
  return {
    id: 'match_winner_random',
    title: `${player.name} · ${player.color} · ${player.factionId} · ${player.selectedSkin} · ${resultText}`,
    kind: REWARD_MODAL_KINDS.MATCH_WINNER,
    player,
    finalWinPoints,
    isRanked,
    ...ratingInfo,
  };
};
